import React, { useEffect, useState } from 'react';
import { Space, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import CustomTable from '../../../../../../../components/CustomTable';
import CustomModal from '../../../../../../../components/CustomModal';
import CustomInput from '../../../../../../../components/CustomInput';
import CustomSelect from '../../../../../../../components/CustomSelect/CustomSelect';
import {
    oneClient,
    getCrmUsersV2 as getCrmUsers,
    clientCrmUsers,
    clientCrmUsersStatus,
    createCrmUserV2 as createCrmUser,
    updateCrmUserV2 as updateCrmUser,
    deleteCrmUserV2 as deleteCrmUser,
    getRoles,
    allRoles,
} from '../../../../../../../redux/slices/clientsSlice';

export default function CrmUsersPage() {
    const dispatch = useDispatch();

    const client = useSelector(oneClient);
    const crmUsers = useSelector(clientCrmUsers);
    const crmUsersStatus = useSelector(clientCrmUsersStatus);
    const roles = useSelector(allRoles);

    const [data, setData] = useState({
        email: '',
        password: '',
        role_id: '',
        is_service_account: true,
    });
    const [crmUserId, setCrmUserId] = useState(null);
    const [crmUserEmail, setCrmUserEmail] = useState('');
    const [isAddCrmUserModalVisible, setIsAddCrmUserModalVisible] =
        useState(false);
    const [isEditCrmUserModalVisible, setIsEditCrmUserModalVisible] =
        useState(false);
    const [isDeleteCrmUserModalVisible, setIsDeleteCrmUserModalVisible] =
        useState(false);
    const [errors, setErrors] = useState({});
    const [selectOptionsRoles, setSelectOptionsRoles] = useState([]);
    const modifiedUsersForTable = crmUsers?.map((el) => {
        return {
            ...el,
            role_title: el?.role?.title,
        };
    });

    useEffect(() => {
        if (client.api_url) {
            dispatch(
                getCrmUsers({ url: client.api_url, client: client.client_uid }),
            );
            dispatch(
                getRoles({ url: client.api_url, client: client.client_uid }),
            );
        }
    }, [client.api_url, client.client_uid, dispatch]);

    useEffect(() => {
        if (roles) {
            const selectOptions = roles?.reduce((acc, el) => {
                acc.push({
                    id: el.id,
                    value: el.title.toLowerCase(),
                    label: el.title,
                });

                return acc;
            }, []);
            setSelectOptionsRoles(selectOptions);
            setData((prev) => ({ ...prev, role_id: selectOptions[0]?.id }));
        }
    }, [roles]);

    const toggleAddCrmUserModal = () => {
        resetState();
        setErrors({});
        setIsAddCrmUserModalVisible(!isAddCrmUserModalVisible);
    };

    const toggleEditCrmUserModal = (e, record) => {
        if (!record) {
            setIsEditCrmUserModalVisible(!isEditCrmUserModalVisible);
            resetState();
        } else {
            setData({
                ...data,
                email: record.email,
                role_id: record.role?.id,
                is_service_account: record.is_service_account,
            });
            setCrmUserId(record.id);
            setErrors({});
            setIsEditCrmUserModalVisible(!isEditCrmUserModalVisible);
        }
    };

    const toggleDeleteCrmUserModal = (e, record) => {
        if (!record) {
            setIsDeleteCrmUserModalVisible(!isDeleteCrmUserModalVisible);
            resetState();
        } else {
            setCrmUserId(record.id);
            setCrmUserEmail(record.email);
            setErrors({});
            setIsDeleteCrmUserModalVisible(!isDeleteCrmUserModalVisible);
        }
    };

    const resetState = () => {
        setData({
            role_id: selectOptionsRoles[0]?.id,
            email: '',
            password: '',
            is_service_account: false,
        });
    };

    const onInputChange = (e) => {
        const { id, value } = e.target;

        setData({ ...data, [id]: value });
    };

    const onCheckboxChange = (e) => {
        setData({
            ...data,
            is_service_account: e.target.checked,
        });
    };

    const onAddCrmUser = async () => {
        const request = {
            body: data,
            url: client.api_url,
            client: client.client_uid,
        };
        try {
            await dispatch(createCrmUser(request)).unwrap();
            toast.success('New theme successfully added');
            toggleAddCrmUserModal();
            dispatch(
                getCrmUsers({ url: client.api_url, client: client.client_uid }),
            );
        } catch (error) {
            console.log(error);
            setErrors(error.errors);
        }
    };

    const onEditCrmUser = async () => {
        const request = {
            id: crmUserId,
            body: data.password
                ? {
                      email: data.email,
                      password: data.password,
                      role_id: data.role_id,
                      is_service_account: data.is_service_account,
                  }
                : {
                      email: data.email,
                      role_id: data.role_id,
                      is_service_account: data.is_service_account,
                  },
            url: client.api_url,
            client: client.client_uid,
        };
        try {
            await dispatch(updateCrmUser(request)).unwrap();
            toast.success('Changes saved successfully');
            dispatch(
                getCrmUsers({ url: client.api_url, client: client.client_uid }),
            );
            toggleEditCrmUserModal();
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteCrmUser = async () => {
        const request = {
            id: crmUserId,
            url: client.api_url,
            client: client.client_uid,
        };
        try {
            await dispatch(deleteCrmUser(request)).unwrap();
            toast.success('User deleted successfully');
            dispatch(
                getCrmUsers({ url: client.api_url, client: client.client_uid }),
            );
            toggleDeleteCrmUserModal();
        } catch (error) {
            toast.error(error.errors[0].message);
            toggleDeleteCrmUserModal();
        }
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined
                    onClick={(e) => toggleEditCrmUserModal(e, record)}
                />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteCrmUserModal(e, record)}
                />
            </Space>
        ),
    };

    const emailOption = {
        title: 'Email',
        render: (_, record) => (
            <span>
                {record.email}
                {!!record.is_service_account && ' (SERVICE ACCOUNT)'}
            </span>
        ),
    };

    const getFilteredCrmUsers = (data, filter) =>
        data?.filter((el) =>
            el?.email.toLowerCase().includes(filter.toLowerCase()),
        );

    const onSelectChange = (option) => {
        setData({ ...data, role_id: option.id });
    };

    return (
        <div>
            <Helmet>
                <title>{`Console Defentry - Clients - ${client.name} - CRM - Users`}</title>
            </Helmet>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['ID', 'Email', 'Role', 'Actions']}
                    columnsDataMap={{
                        Role: 'role_title',
                    }}
                    functionalCell={{
                        Actions: actionsOption,
                        Email: emailOption,
                    }}
                    onAddButtonClick={toggleAddCrmUserModal}
                    tablePannelTitle="Table of CRM Users"
                    searchInputPlaceholder="Search by email"
                    topPannelButtonText="Add CRM User"
                    data={modifiedUsersForTable}
                    getFilteredData={getFilteredCrmUsers}
                    isLoading={crmUsersStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Add new CRM User"
                subText="Please enter CRM User details"
                subTextAlign="left"
                isModalVisible={isAddCrmUserModalVisible}
                onAcceptButtonClick={onAddCrmUser}
                onCancelButtonClick={toggleAddCrmUserModal}
                cancelButtonText="Cancel"
                acceptButtonText="Add"
            >
                <CustomInput
                    id="email"
                    placeholder="Enter user email"
                    onInputChange={onInputChange}
                    inputValue={data.email}
                    labelText="Email"
                    width="100%"
                    errorText={errors?.email}
                />
                <div style={{ marginTop: '16px' }} />
                <CustomInput
                    id="password"
                    type="password"
                    placeholder="Enter user password"
                    onInputChange={onInputChange}
                    inputValue={data.password}
                    labelText="User password"
                    width="100%"
                    errorText={errors?.password}
                />
                <div style={{ marginTop: '16px' }} />
                <p className="input-label">Role</p>
                <CustomSelect
                    name="roles"
                    onChange={onSelectChange}
                    options={selectOptionsRoles}
                    placeholder="Select role"
                    value={data.role_id}
                    errorText={errors?.role_id}
                />
                <div style={{ marginTop: '16px' }} />
                <div
                    className="form__item-wrapper--column"
                    style={{ marginBottom: 0 }}
                >
                    <p className="input-label">Is service account</p>
                    <div style={{ width: '50px' }}>
                        <Checkbox
                            onChange={onCheckboxChange}
                            checked={data.is_service_account}
                        >
                            Yes
                        </Checkbox>
                    </div>
                </div>
            </CustomModal>
            <CustomModal
                title="Edit CRM User"
                subText="Edit CRM User details below"
                subTextAlign="left"
                isModalVisible={isEditCrmUserModalVisible}
                onAcceptButtonClick={onEditCrmUser}
                onCancelButtonClick={toggleEditCrmUserModal}
                cancelButtonText="Cancel"
                acceptButtonText="Save"
            >
                <CustomInput
                    id="email"
                    placeholder="Enter user email"
                    onInputChange={onInputChange}
                    inputValue={data.email}
                    labelText="Email"
                    width="100%"
                    errorText={errors?.email}
                />
                <div style={{ marginTop: '16px' }} />
                <CustomInput
                    id="password"
                    type="password"
                    placeholder="Enter user password"
                    onInputChange={onInputChange}
                    inputValue={data.password}
                    labelText="User password"
                    width="100%"
                    errorText={errors?.password}
                />
                <div style={{ marginTop: '16px' }} />
                <p className="input-label">Role</p>
                <CustomSelect
                    name="roles"
                    onChange={onSelectChange}
                    options={selectOptionsRoles}
                    placeholder="Select role"
                    value={data.role_id}
                    errorText={errors?.role_id}
                />
                <div style={{ marginTop: '16px' }} />
                <div
                    className="form__item-wrapper--column"
                    style={{ marginBottom: 0 }}
                >
                    <p className="input-label">Is service account</p>
                    <div style={{ width: '50px' }}>
                        <Checkbox
                            onChange={onCheckboxChange}
                            checked={data.is_service_account}
                        >
                            Yes
                        </Checkbox>
                    </div>
                </div>
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${crmUserEmail} `}</span>
                        user?
                    </p>
                }
                subText="You will delete this user without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteCrmUserModalVisible}
                onAcceptButtonClick={onDeleteCrmUser}
                onCancelButtonClick={toggleDeleteCrmUserModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            />
        </div>
    );
}
